// ─── Project Tracking (Issue #183) ───────────────────────────────────────────

import type {
  Project,
  ProjectMilestone,
  ProjectOutcome,
  ProjectStatus,
  ProjectOutcomeStatus,
} from '../../graphql/types';
import { createEntityCrudClient } from './crudFactory';

export interface CreateProjectInput {
  name: string;
  description?: string;
  status: ProjectStatus;
  ownerDivision: string;
  ownerTeamId?: string;
  startDate?: string;
  targetEndDate?: string;
}

export interface UpdateProjectInput {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  ownerDivision?: string;
  ownerTeamId?: string;
  startDate?: string;
  targetEndDate?: string;
}

const projectClient = createEntityCrudClient<Project, CreateProjectInput, UpdateProjectInput>(
  '/projects'
);

export async function createProject(input: CreateProjectInput): Promise<Project> {
  return projectClient.create(input);
}

export async function updateProject(
  projectId: string,
  input: UpdateProjectInput
): Promise<Project> {
  return projectClient.update(projectId, input);
}

export async function deleteProject(projectId: string): Promise<void> {
  return projectClient.remove(projectId);
}

/**
 * Wire-format milestone status accepted by the REST write routes
 * (`backend/src/entities/ProjectMilestone.ts` `ProjectMilestoneStatus`
 * nativeEnum: 'not-started' | 'in-progress' | 'complete' | 'slipped').
 *
 * Distinct on purpose from the GraphQL-facing `ProjectMilestoneStatus` enum
 * in graphql/types/project.ts, whose `NotStarted`/`InProgress` members serialize to
 * the *external* enum names `not_started`/`in_progress` for reads (see
 * backend resolvers.ts enum value config) — sending those values on a REST
 * write fails Zod validation (same class of bug as `AssignmentStatusInput`,
 * issue #535).
 */
export type ProjectMilestoneStatusInput = 'not-started' | 'in-progress' | 'complete' | 'slipped';

export interface CreateProjectMilestoneInput {
  projectId: string;
  name: string;
  sequence: number;
  targetDate?: string;
  status: ProjectMilestoneStatusInput;
  description?: string;
}

export interface UpdateProjectMilestoneInput {
  name?: string;
  sequence?: number;
  targetDate?: string;
  status?: ProjectMilestoneStatusInput;
  description?: string;
}

const milestoneClient = createEntityCrudClient<
  ProjectMilestone,
  CreateProjectMilestoneInput,
  UpdateProjectMilestoneInput
>('/project-milestones');

export async function createProjectMilestone(
  input: CreateProjectMilestoneInput
): Promise<ProjectMilestone> {
  return milestoneClient.create(input);
}

export async function updateProjectMilestone(
  milestoneId: string,
  input: UpdateProjectMilestoneInput
): Promise<ProjectMilestone> {
  return milestoneClient.update(milestoneId, input);
}

export async function deleteProjectMilestone(milestoneId: string): Promise<void> {
  return milestoneClient.remove(milestoneId);
}

export interface CreateProjectOutcomeInput {
  projectId: string;
  name: string;
  description?: string;
  displayOrder?: number;
  status: ProjectOutcomeStatus;
}

export interface UpdateProjectOutcomeInput {
  name?: string;
  description?: string;
  displayOrder?: number;
  status?: ProjectOutcomeStatus;
}

const outcomeClient = createEntityCrudClient<
  ProjectOutcome,
  CreateProjectOutcomeInput,
  UpdateProjectOutcomeInput
>('/project-outcomes');

export async function createProjectOutcome(
  input: CreateProjectOutcomeInput
): Promise<ProjectOutcome> {
  return outcomeClient.create(input);
}

export async function updateProjectOutcome(
  outcomeId: string,
  input: UpdateProjectOutcomeInput
): Promise<ProjectOutcome> {
  return outcomeClient.update(outcomeId, input);
}

export async function deleteProjectOutcome(outcomeId: string): Promise<void> {
  return outcomeClient.remove(outcomeId);
}
